import React from "react";
import { Box, Typography, Stack, useTheme } from "@mui/material";
import { MaleIcon, FemaleIcon, OtherIcon } from "../../../assets/icons";
import type { DistributionItem } from "../../../shared/api/types";

interface DemographicsChartProps {
  data: DistributionItem[];
  title?: string;
}

const CHART_COLORS = ["#4DD0E1", "#FF9800", "#1976D2", "#4CAF50", "#F44336"];

const getIcon = (label: string) => {
  const normalizedLabel = label.toLowerCase();
  if (normalizedLabel === "male") return MaleIcon;
  if (normalizedLabel === "female") return FemaleIcon;
  return OtherIcon;
};

export const DemographicsChart: React.FC<DemographicsChartProps> = ({
  data,
  title,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Chart Rows */}
      <Stack
        spacing={2}
        sx={{ mb: 3 }}
        flex={1}
        height="100%"
        justifyContent="center"
      >
        {data.map((item, index) => {
          const Icon = getIcon(item.label);
          const color = CHART_COLORS[index % CHART_COLORS.length];
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* Icons Row */}
              <Box
                sx={{
                  display: "flex",
                  paddingRight: 2,
                  width: "100%",
                }}
              >
                {Array.from({ length: 10 }).map((_, i) => {
                  // Each icon represents 10%. Fill proportionally so small
                  // percentages (e.g. 4%) still show a partial icon rather than
                  // rounding to zero — fill fraction of the icon at index i.
                  const fillUnits = (item.percentage / 100) * 10;
                  const fraction = Math.min(Math.max(fillUnits - i, 0), 1);
                  return (
                    <Box
                      key={i}
                      sx={{
                        position: "relative",
                        flex: 1,
                        flexShrink: 0,
                        maxWidth: "40px",
                        display: "flex",
                      }}
                    >
                      {/* Base (empty) icon */}
                      <Icon
                        sx={{
                          height: "auto",
                          width: "100%",
                          color: theme.palette.action.disabledBackground,
                        }}
                      />
                      {/* Filled overlay, clipped to the fill fraction */}
                      {fraction > 0 && (
                        <Icon
                          sx={{
                            position: "absolute",
                            inset: 0,
                            height: "auto",
                            width: "100%",
                            color,
                            clipPath: `inset(0 ${(1 - fraction) * 100}% 0 0)`,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
              {/* Percentage */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: "40px", textAlign: "left", flexShrink: 0 }}
              >
                {item.percentage.toString().padStart(2, "0")}%
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        {data.map((item, index) => {
          const Icon = getIcon(item.label);
          const color = CHART_COLORS[index % CHART_COLORS.length];
          return (
            <Box
              key={index}
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {item.label}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ fontSize: 20, color: color }} />
                <Typography variant="subtitle2">{item.value}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
